import { Text } from "@nova/protobuf"
import { generateGenericCases } from "../../generateCases"
import { Builder } from "../../sockets"
import {
    CreateTextArgs,
    getDefaultTextCreateArgs,
    UpdateTextArgs,
} from "@nova/serializer/src/client/validators/cubes/text"
import {
    addEntitySocket,
    deleteEntitySocket,
    updateEntitySocket,
} from "features/entity-caching/project/slice"
import { v4 as randomUUID } from "uuid"
import { setIsEditingText } from "plugins/Toolbox/Text/slice"
import { ToolName } from "@nova/constants"
import { setSelectedTool } from "plugins/Toolbox/actions"
import { selectSingleLayer } from "panels/content/side-panel/slice.new"
import { maybeSetProjectLocation } from "../projectLocation"

const {
    create: createText,
    update: updateText,
    delete: deleteText,
    createAction,
    updateAction,
    deleteAction,
} = generateGenericCases<
    Text, // entity type
    CreateTextArgs, // create args to send to server
    [
        "longitude",
        "latitude",
        "color",
        "bold",
        "text",
        "fontSize",
        "baseFontSize",
        "rotation",
        "outlineColor",
        "outlineWidth",
        "createdBy",
        "parentId",
    ], // create args required by action
    UpdateTextArgs, // update args
    { name?: string; isDuplicated?: boolean },
    void
>("Text")

export const addTextsToBuilder = (builder: Builder) =>
    builder
        .addCase(createAction, (store, { payload: { data, context } }, socket) => {
            const state = store.getState()
            const texts = state.project.data.texts
            const name = context?.name || `Text ${Object.values(texts).length + 1}`

            const optimisticData: Text = {
                name,
                id: randomUUID(),
                ...getDefaultTextCreateArgs(store.getState().project.data, data.parentId),
                ...data,
            }
            createText(socket, {
                ...data,
                name,
                id: optimisticData.id,
                index: optimisticData.index,
            })
            store.dispatch(
                addEntitySocket({
                    data: optimisticData,
                    path: ["texts"],
                })
            )
            const project = store.getState().project.data
            maybeSetProjectLocation(store, project.orgId, project.id, {
                latitude: optimisticData.latitude,
                longitude: optimisticData.longitude,
            })

            store.dispatch(selectSingleLayer({ id: optimisticData.id }))
            if (!context?.isDuplicated) {
                store.dispatch(setSelectedTool({ tool: optimisticData, toolName: ToolName.Text }))
                store.dispatch(setIsEditingText(true))
            }
        })
        .addCase(updateAction, (store, { payload: { id, updates } }, socket) => {
            updateText(socket, id, updates)
            store.dispatch(updateEntitySocket({ updates, path: ["texts", { id }] }))
        })
        .addCase(deleteAction, (store, { payload: { id } }, socket) => {
            deleteText(socket, id)
            store.dispatch(deleteEntitySocket({ path: ["texts", { id }] }))
        })

export { createAction as createText, updateAction as updateText, deleteAction as deleteText }
